import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButtonEmpty, EuiCallOut, EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiLink, EuiSpacer, EuiTitle } from '@elastic/eui'

import { useUpdateCampaignLocationTargetingMutation } from '../../../../../api/rtkQueryApi/platform/campaignsApi'
import { showSuccessToast } from '../../../../../app/appSlice'
import { RootState } from '../../../../../app/rootReducer'
import { AcFileUploader } from '../../../../../components/Basic/AcFileUploader'
import { LocationType } from '../../../../../components/targeters/GeoSelectionTargeter'
import { useAudienceUploadLimit } from '../../../../../hooks/useAudienceUploadLimit'
import { GetLocationTargeting } from '../../../../../utils/CampaignFormatter'
import { getUploadedAudienceFileName } from '../../../../../utils/uploadedAudienceFileName'
import { fetchCampaign } from '../../campaignSlice'
import { useCampaignEdit } from '../CampaignEditContext'

const ALLOWED_AUDIENCE_FILE_TYPES = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/tab-separated-values', 'text/plain']

const ALLOWED_AUDIENCE_FILE_ACCEPT = '.csv,.xls,.xlsx,.tsv,.txt'

export const EditUploadedAudience = () => {
  const audienceUploadLimit = useAudienceUploadLimit()
  const { campaign } = useSelector((state: RootState) => state.campaign)
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const [updateTargeting] = useUpdateCampaignLocationTargetingMutation()
  const dispatch = useDispatch()
  const { registerSave, markDirty } = useCampaignEdit()
  const [uploadedAudienceUrl, setUploadedAudienceUrl] = useState(GetLocationTargeting(campaign ?? undefined).uploadedAudienceUrl)
  const [isInvalidAudienceUpload, setIsInvalidAudienceUpload] = useState(false)

  useEffect(() => {
    if (campaign) {
      setUploadedAudienceUrl(GetLocationTargeting(campaign).uploadedAudienceUrl)
    }
  }, [campaign])

  const onSave = useCallback(async () => {
    if (!campaign || !currentAccount) {
      return
    }
    updateTargeting({
      campaignId: campaign.id,
      accountId: currentAccount.id,
      targeting: {
        locations: {
          countries: [],
          regions: [],
          dmas: [],
          cities: [],
          postalCodes: [],
          uploadedAudienceUrl
        },
        geoCircles: [],
        geoRectangles: [],
        geoPolygons: [],
        geoFences: []
      },
      locationType: LocationType.USA,
      audience: {
        ageRanges: [],
        genders: [],
        householdIncomes: [],
        thirdPartyAudiences: [],
        segments: []
      }
    }).then(() => {
      dispatch(showSuccessToast('Saved uploaded audience'))
      dispatch(fetchCampaign(currentAccount.id, campaign.id))
    })
  }, [campaign, currentAccount, uploadedAudienceUrl, updateTargeting, dispatch])

  useEffect(() => {
    registerSave('uploadedAudience', onSave)
  }, [onSave, registerSave])

  const onUploadedAudienceUrlChange = (url: string) => {
    setUploadedAudienceUrl(url)
    markDirty('uploadedAudience')
  }

  const onRemoveUploadedAudience = () => {
    setUploadedAudienceUrl(undefined)
    markDirty('uploadedAudience')
  }

  const uploadedAudienceFileName = getUploadedAudienceFileName(uploadedAudienceUrl)

  if (!campaign) {
    return null
  }

  return (
    <>
      <EuiCallOut title='Targeting your uploaded audience' iconType='users' color='primary'>
        <p>Audience targeting set with an uploaded list of PII.</p>
      </EuiCallOut>
      <EuiSpacer />
      <EuiTitle size={'xs'}>
        <h3>Upload audience list</h3>
      </EuiTitle>
      <EuiSpacer size='xs' />
      <EuiFormRow fullWidth helpText='Upload a CSV, Excel, or text list file, with one record per line.'>
        <AcFileUploader maxFileSize={audienceUploadLimit.maxFileSize} allowedFileTypes={ALLOWED_AUDIENCE_FILE_TYPES} accept={ALLOWED_AUDIENCE_FILE_ACCEPT} onUploadedUrlChange={onUploadedAudienceUrlChange} onIsValidFileChange={isValid => setIsInvalidAudienceUpload(!isValid)} />
      </EuiFormRow>
      {isInvalidAudienceUpload && (
        <>
          <EuiSpacer size='s' />
          <EuiCallOut title={`Please upload a CSV, Excel, or text list file under ${audienceUploadLimit.label}`} iconType='document' color='warning' />
        </>
      )}
      {uploadedAudienceFileName && (
        <>
          <EuiSpacer size='s' />
          <EuiFormRow fullWidth label='Current uploaded list'>
            <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
              <EuiFlexItem grow={false}>
                <EuiLink href={uploadedAudienceUrl} target='_blank'>
                  {uploadedAudienceFileName}
                </EuiLink>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButtonEmpty size='xs' color='danger' iconType='trash' onClick={onRemoveUploadedAudience}>
                  Remove
                </EuiButtonEmpty>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFormRow>
        </>
      )}
    </>
  )
}
